/**
 * TankCatalogPage — Task 25.6 (DEC-117). PLATFORM_ADMIN standard tank catalog.
 *
 * Table of all standard tank types (incl. inactive), an Active/Inactive filter,
 * Add / Edit (modal), and Deactivate/Reactivate via PATCH { isActive }. Uses ONLY
 * the existing Task 24.5 endpoints — no new API.
 *
 * DEC-379 (0265): no Type column. A row whose BBL per inch is more than 5% from its
 * dimension-based estimate shows a warning icon by the value, its tooltip giving the
 * percentage (shared bblPerInchWarning, the same check as the form).
 */
import * as React from 'react';
import { toast } from 'sonner';
import { TriangleAlert } from 'lucide-react';
import { bblPerInchWarning } from '@lodex-pro/shared/tank-strapping';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from 'src/components/ui/table';
import { Button } from 'src/components/ui/button';
import { SortableHead } from 'src/components/ui/sortable-head';
import { sortRows, useUrlSort } from '../../lib/url-state';
import { AddButton, Pill, subText } from '../../ui/bits';
import { useStandardTanks, useUpdateTank, type StandardTank } from './hooks';
import { TankModal } from './TankModal';
import { formatNumber } from '@lodex-pro/shared/number-format';

type Filter = 'all' | 'active' | 'inactive';

/** The columns the catalog table sorts by (OPEN-204); the API sends them by name. */
const TANK_SORTS = ['name', 'capacity', 'bblPerInch'] as const;

/** The BBL / inch value, with a warning icon when it is far from the dimensions' estimate. */
export function BblPerInchCell({ tank }: { tank: StandardTank }): React.JSX.Element {
  const warning = bblPerInchWarning(tank.bblPerInch, tank);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      {tank.bblPerInch}
      {warning !== null && (
        <span
          role="img"
          aria-label={warning.message}
          title={warning.message}
          data-testid="bpi-warning-icon"
          style={{ display: 'inline-flex', color: 'var(--color-warning)' }}
        >
          <TriangleAlert size={16} aria-hidden="true" />
        </span>
      )}
    </span>
  );
}

/** Deactivate/reactivate a single row (its own hook instance). */
function StatusAction({ tank }: { tank: StandardTank }): React.JSX.Element {
  const update = useUpdateTank(tank.id);

  const onDeactivate = (): void => {
    if (
      !window.confirm(
        `Deactivate "${tank.name}"? Existing hauler tanks are unaffected, but haulers will no longer see this option when adding new tanks.`,
      )
    ) {
      return;
    }
    update.mutate(
      { isActive: false },
      {
        onSuccess: () => toast.success('Tank type deactivated'),
        onError: () => toast.error('Could not deactivate'),
      },
    );
  };
  const onReactivate = (): void => {
    update.mutate(
      { isActive: true },
      {
        onSuccess: () => toast.success('Tank type reactivated'),
        onError: () => toast.error('Could not reactivate'),
      },
    );
  };

  return tank.isActive ? (
    <Button
      type="button"
      variant="outline"
      disabled={update.isPending}
      onClick={onDeactivate}
      style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
    >
      Deactivate
    </Button>
  ) : (
    <Button type="button" variant="outline" disabled={update.isPending} onClick={onReactivate}>
      Reactivate
    </Button>
  );
}

export function TankCatalogPage(): React.JSX.Element {
  const { data, isLoading, isError } = useStandardTanks();
  const [filter, setFilter] = React.useState<Filter>('all');
  const [modalOpen, setModalOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<StandardTank | null>(null);

  const { sort, toggle } = useUrlSort(TANK_SORTS, { key: 'name', dir: 'asc' }, []);

  const shown = React.useMemo(() => {
    const all = data?.standardTanks ?? [];
    if (filter === 'active') return all.filter((t) => t.isActive);
    if (filter === 'inactive') return all.filter((t) => !t.isActive);
    return all;
  }, [data, filter]);
  // Numeric columns arrive as strings; they sort as numbers.
  const tanks = sortRows(shown, sort, (t, k) => {
    switch (k) {
      case 'name':
        return t.name;
      case 'capacity':
        return Number(t.capacityBbl);
      case 'bblPerInch':
        return Number(t.bblPerInch);
    }
  });

  const openAdd = (): void => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (t: StandardTank): void => {
    setEditing(t);
    setModalOpen(true);
  };

  const FILTERS: readonly { id: Filter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'inactive', label: 'Inactive' },
  ];

  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Standard Tank Catalog</h2>
          <p style={{ ...subText, marginTop: '2px' }}>{tanks.length} tank types</p>
        </div>
        <AddButton label="Add Tank Type" onClick={openAdd} />
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '16px' }}>
        {FILTERS.map((f) => (
          <Button
            key={f.id}
            type="button"
            variant={filter === f.id ? 'default' : 'outline'}
            onClick={(): void => setFilter(f.id)}
            style={
              filter === f.id
                ? { backgroundColor: 'var(--color-accent-blue)', color: 'var(--color-on-accent)' }
                : undefined
            }
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading && <p style={subText}>Loading tank catalog…</p>}
      {isError && (
        <p role="alert" style={{ color: 'var(--color-danger)' }}>
          Could not load the tank catalog. Refresh to try again.
        </p>
      )}

      {!isLoading && !isError && (
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHead column="name" sort={sort} onSort={toggle}>
                Name
              </SortableHead>
              <SortableHead column="capacity" sort={sort} onSort={toggle}>
                Capacity (BBL)
              </SortableHead>
              <SortableHead column="bblPerInch" sort={sort} onSort={toggle}>
                BBL / inch
              </SortableHead>
              <TableHead>Status</TableHead>
              <TableHead style={{ textAlign: 'right' }}>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tanks.map((t) => (
              <TableRow key={t.id}>
                <TableCell style={{ fontWeight: 500 }}>{t.name}</TableCell>
                <TableCell>{formatNumber(t.capacityBbl)}</TableCell>
                <TableCell>
                  <BblPerInchCell tank={t} />
                </TableCell>
                <TableCell>
                  <Pill
                    label={t.isActive ? 'Active' : 'Inactive'}
                    tone={t.isActive ? 'positive' : 'muted'}
                  />
                </TableCell>
                <TableCell>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <Button type="button" variant="outline" onClick={(): void => openEdit(t)}>
                      Edit
                    </Button>
                    <StatusAction tank={t} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {tanks.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} style={{ ...subText, textAlign: 'center', padding: '32px' }}>
                  No tank types in this view.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <TankModal open={modalOpen} onOpenChange={setModalOpen} editing={editing} />
    </div>
  );
}
